import {
    AlertCircle,
    RotateCcw,
    ChevronDown,
    ChevronUp,
    Target,
    Shield,
    Users,
    Zap,
    Trophy,
    Calculator,
} from "lucide-react";
import { useState } from "react";
import { Loader } from "@/components/general/common/loader";
import { Multiplier, MultipliersResponse, CategoryData } from "@/api/multipliers";
import { KPIGroup } from "./KPIGroup";

interface SettingsListProps {
    multipliersData: MultipliersResponse | undefined;
    editedMultipliers: Map<number, Partial<Multiplier>>;
    setEditedMultipliers: (map: Map<number, Partial<Multiplier>>) => void;
    isLoading: boolean;
    error: Error | null;
    onRefresh: () => void;
}

export function SettingsList({
    multipliersData,
    editedMultipliers,
    setEditedMultipliers,
    isLoading,
    error,
    onRefresh,
}: SettingsListProps) {
    const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(
        new Set([
            "offensive",
            "defensive",
            "teamplay",
            "effort",
            "bonus",
            "total",
            "gsAx",
            "highDangerShots",
            "lowDangerShots",
            "lateralPassShots",
            "screenedShots",
        ])
    );

    const handleInputChange = (
        id: number,
        field: keyof Pick<Multiplier, "weight" | "maxPoints" | "isActive">,
        value: string | boolean
    ) => {
        const newEdited = new Map(editedMultipliers);
        const current = newEdited.get(id) || {};

        if (field === "weight" || field === "maxPoints") {
            const stringValue = value as string;

            if (stringValue === "") {
                newEdited.set(id, { ...current, [field]: null });
            } else {
                const numValue = parseFloat(stringValue);

                if (isNaN(numValue) || !isFinite(numValue)) {
                    return;
                }

                newEdited.set(id, { ...current, [field]: numValue });
            }
        } else if (field === "isActive") {
            newEdited.set(id, { ...current, [field]: value as boolean });
        }

        setEditedMultipliers(newEdited);
    };

    const resetToDefault = (multiplier: Multiplier) => {
        const newEdited = new Map(editedMultipliers);
        newEdited.set(multiplier.id, {
            weight: multiplier.defaultWeight,
            maxPoints: multiplier.defaultMaxPoints,
            isActive: multiplier.isActive,
        });
        setEditedMultipliers(newEdited);
    };

    const toggleCategory = (categoryName: string) => {
        const newCollapsed = new Set(collapsedCategories);
        if (newCollapsed.has(categoryName)) {
            newCollapsed.delete(categoryName);
        } else {
            newCollapsed.add(categoryName);
        }
        setCollapsedCategories(newCollapsed);
    };

    const formatCategoryName = (categoryName: string) => {
        const nameMap: Record<string, string> = {
            gsAx: "GSAx",
            highDangerShots: "High Danger Shots",
            lowDangerShots: "Low Danger Shots",
            lateralPassShots: "Lateral Pass Shots",
            screenedShots: "Screened Shots",
        };
        return nameMap[categoryName] ?? categoryName.charAt(0).toUpperCase() + categoryName.slice(1);
    };

    const getCategoryIcon = (categoryName: string) => {
        const iconMap = {
            offensive: <Target className="w-5 h-5" />,
            defensive: <Shield className="w-5 h-5" />,
            teamplay: <Users className="w-5 h-5" />,
            effort: <Zap className="w-5 h-5" />,
            bonus: <Trophy className="w-5 h-5" />,
            total: <Calculator className="w-5 h-5" />,
            gsAx: <Shield className="w-5 h-5" />,
            highDangerShots: <Target className="w-5 h-5" />,
            lowDangerShots: <Target className="w-5 h-5" />,
            lateralPassShots: <Target className="w-5 h-5" />,
            screenedShots: <Target className="w-5 h-5" />,
        };
        return iconMap[categoryName as keyof typeof iconMap] || <Calculator className="w-5 h-5" />;
    };

    const getCategoryColor = (categoryName: string) => {
        const colorMap = {
            offensive: "var(--color-general-red)",
            defensive: "var(--color-general-green)",
            teamplay: "var(--color-general-yellow)",
            effort: "var(--color-general-purple)",
            bonus: "var(--color-general-orange)",
            total: "var(--color-general-blue)",
            gsAx: "var(--color-general-green)",
            highDangerShots: "var(--color-general-red)",
            lowDangerShots: "var(--color-general-yellow)",
            lateralPassShots: "var(--color-general-purple)",
            screenedShots: "var(--color-general-orange)",
        };
        return colorMap[categoryName as keyof typeof colorMap] || "var(--color-general-blue)";
    };

    if (!multipliersData && isLoading) {
        return (
            <div className="flex-1 min-h-0 flex flex-col">
                <div className="flex-1 flex items-center justify-center min-h-0 bg-surface-container-lowest rounded-xl shadow-sm p-6">
                    <Loader message="Loading multipliers..." />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex-1 min-h-0 flex flex-col">
                <div className="flex-1 flex items-center justify-center min-h-0 bg-surface-container-lowest rounded-xl shadow-sm p-6">
                    <div className="flex flex-col items-center text-center">
                        <AlertCircle className="w-12 h-12 text-general-red mb-4" />
                        <h2 className="text-xl font-headline font-bold text-on-surface mb-2">
                            Failed to Load Multipliers
                        </h2>
                        <p className="text-on-surface-variant mb-4">{error.message}</p>
                        <button
                            onClick={onRefresh}
                            className="px-4 py-2 bg-general-blue text-white rounded-md font-headline font-bold uppercase tracking-wider text-xs hover:opacity-90 transition-all flex items-center gap-2"
                        >
                            <RotateCcw className="w-4 h-4" />
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!multipliersData) {
        return null;
    }

    const categoryEntries = Object.entries(multipliersData) as Array<[string, CategoryData[] | Multiplier]>;

    const playerCategories = ["offensive", "defensive", "teamplay", "effort", "bonus", "total"];
    const goalkeeperCategories = ["gsAx", "highDangerShots", "lowDangerShots", "lateralPassShots", "screenedShots"];

    const playerEntries = categoryEntries.filter(([name]) => playerCategories.includes(name));
    const goalkeeperEntries = categoryEntries.filter(([name]) => goalkeeperCategories.includes(name));

    const renderCategory = ([categoryName, categoryData]: [string, CategoryData[] | Multiplier]) => {
        if (categoryName === "total" || goalkeeperCategories.includes(categoryName)) {
            if (categoryData && !Array.isArray(categoryData)) {
                const multiplier = categoryData as Multiplier;
                const isCollapsed = collapsedCategories.has(categoryName);

                return (
                    <div
                        key={categoryName}
                        className="bg-surface-container-lowest rounded-xl shadow-sm border border-outline-variant/30 overflow-hidden"
                    >
                        <button
                            onClick={() => toggleCategory(categoryName)}
                            className="w-full flex items-center justify-between p-3 sm:p-4 cursor-pointer bg-surface-container-high"
                        >
                            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                                <div
                                    className="p-2 rounded-md text-white flex-shrink-0"
                                    style={{ backgroundColor: getCategoryColor(categoryName) }}
                                >
                                    {getCategoryIcon(categoryName)}
                                </div>
                                <h2 className="text-sm font-headline font-black text-primary uppercase tracking-widest truncate">
                                    {formatCategoryName(categoryName)}
                                </h2>
                            </div>
                            <div className="flex items-center gap-2">
                                {isCollapsed ? (
                                    <ChevronDown className="w-5 h-5 text-on-surface-variant" />
                                ) : (
                                    <ChevronUp className="w-5 h-5 text-on-surface-variant" />
                                )}
                            </div>
                        </button>

                        {!isCollapsed && (
                            <div className="p-3 sm:p-4 space-y-3 bg-surface-container-low border-t border-outline-variant/30">
                                <KPIGroup
                                    key={`${categoryName}-${multiplier.id}`}
                                    group={multiplier}
                                    editedMultipliers={editedMultipliers}
                                    onInputChange={handleInputChange}
                                    onResetToDefault={resetToDefault}
                                    isLoading={isLoading}
                                />
                            </div>
                        )}
                    </div>
                );
            }
            return null;
        }

        if (!Array.isArray(categoryData) || categoryData.length === 0) return null;

        const categoryGroups = categoryData as CategoryData[];
        const isCollapsed = collapsedCategories.has(categoryName);

        return (
            <div
                key={categoryName}
                className="bg-surface-container-lowest rounded-xl shadow-sm border border-outline-variant/30 overflow-hidden"
            >
                <button
                    onClick={() => toggleCategory(categoryName)}
                    className="w-full flex items-center justify-between p-3 sm:p-4 cursor-pointer bg-surface-container-high"
                >
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                        <div
                            className="p-2 rounded-md text-white flex-shrink-0"
                            style={{ backgroundColor: getCategoryColor(categoryName) }}
                        >
                            {getCategoryIcon(categoryName)}
                        </div>
                        <h2 className="text-sm font-headline font-black text-primary uppercase tracking-widest truncate">
                            {formatCategoryName(categoryName)}
                        </h2>
                    </div>
                    <div className="flex items-center gap-2">
                        {isCollapsed ? (
                            <ChevronDown className="w-5 h-5 text-on-surface-variant" />
                        ) : (
                            <ChevronUp className="w-5 h-5 text-on-surface-variant" />
                        )}
                    </div>
                </button>

                {!isCollapsed && (
                    <div className="p-3 sm:p-4 space-y-3 bg-surface-container-low border-t border-outline-variant/30">
                        {categoryGroups.map((group, groupIndex) => (
                            <KPIGroup
                                key={group.max?.id ? `max-${group.max.id}` : `group-${categoryName}-${groupIndex}`}
                                group={group}
                                editedMultipliers={editedMultipliers}
                                onInputChange={handleInputChange}
                                onResetToDefault={resetToDefault}
                                isLoading={isLoading}
                            />
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div id="onboarding-settings-list" className="flex-1 min-h-0 flex flex-col">
            {isLoading ? (
                <div className="flex-1 flex items-center justify-center min-h-0">
                    <Loader message="Loading multipliers..." />
                </div>
            ) : (
                <div className="flex-1 overflow-auto">
                    <div className="space-y-4">
                        <h2 className="text-xl font-headline font-black text-primary mb-4">Player Weights</h2>
                        {playerEntries.map((entry) => renderCategory(entry))}
                        {goalkeeperEntries.length > 0 && (
                            <>
                                <h2 className="text-xl font-headline font-black text-primary mb-4 mt-8">
                                    Goalkeeper Weights
                                </h2>
                                {goalkeeperEntries.map((entry) => renderCategory(entry))}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
